import { createBrowserRouter } from "react-router-dom";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { RedirectMiddleware } from "@/components/redirect/RedirectMiddleware";
import ScrollToTop from "@/components/ui/scroll-to-top";
import { useAnalytics } from '@/hooks/use-analytics';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { useAuth } from '@/components/auth/AuthProvider';
import { useEffect } from 'react';
import { ComparisonProvider } from "@/hooks/use-comparison";
import { ComparisonBar } from "@/components/comparison/ComparisonBar";
import { ComparisonModal } from "@/components/comparison/ComparisonModal";
import Index from "@/pages/Index";
import AllNursingHomes from "@/pages/AllNursingHomes";
import NursingHomePage from "@/pages/NursingHomePage";
import AboutPage from "@/pages/AboutPage";
import PressPage from "@/pages/PressPage";
import CareersPage from "@/pages/CareersPage";
import TermsPage from "@/pages/legal/TermsPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiesPage from "@/pages/legal/CookiesPage";
import CalculatorPage from "@/pages/CalculatorPage";
import FairDealHubPage from "@/pages/FairDealHubPage";
import WaitlistPage from "@/pages/WaitlistPage";
import GuidePage from "@/pages/GuidePage";
import DigitalGuidePage from "@/pages/DigitalGuidePage";
import ConsultationsPage from "@/pages/ConsultationsPage";
import BlogPage from "@/pages/BlogPage";
import ExpertApplicationPage from "@/pages/ExpertApplicationPage";
import ExpertApplicationThankYouPage from "@/pages/ExpertApplicationThankYouPage";
import ExpertMarketplaceTermsPage from "@/pages/legal/ExpertMarketplaceTermsPage";
import FavoritesPage from "@/pages/FavoritesPage";
import ThankYouPage from "@/pages/ThankYouPage";
import EmailVerificationPage from "@/components/auth/verify/EmailVerificationPage";
import ForgotPasswordPage from "@/components/auth/forgot-password/ForgotPasswordPage";
import ResetPasswordPage from "@/components/auth/reset-password/ResetPasswordPage";
import ExpertApplicationsPage from "@/pages/admin/ExpertApplicationsPage";
import AdminToolsPage from "@/pages/admin/AdminToolsPage";
import ClaimsManagementPage from "@/pages/admin/ClaimsManagementPage";
import AdminGuidePage from "@/pages/AdminGuidePage";
import OwnerDashboardPage from "@/pages/OwnerDashboardPage";
import OwnerNursingHomePage from "@/pages/OwnerNursingHomePage";
import SubscriptionPage from "@/pages/owner/SubscriptionPage";
import CountyPage from "@/pages/CountyPage";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import ExpertProfilePage from "./pages/consultations/ExpertProfilePage";
import OwnerRegisterPage from "@/pages/owner/OwnerRegisterPage";
import OverviewPage from "./pages/owner/OverviewPage";
import ProfilePage from "./pages/owner/ProfilePage";
import MediaPage from "./pages/owner/MediaPage";
import OwnerSettingsPage from "./pages/owner/SettingsPage";
 import LeadsPage from "./pages/owner/LeadsPage";
import SettingsPage from "./pages/SettingsPage";
import EmailTestPage from "./pages/EmailTestPage";
import ClaimNursingHomePage from "@/pages/ClaimNursingHomePage";
import GeneralClaimPage from "@/pages/GeneralClaimPage";
import NursingHomeLookupPage from "@/pages/NursingHomeLookupPage";
import AllNursingHomesMapPage from "@/pages/AllNursingHomesMapPage";
import NotFound from "@/pages/NotFound";
import PremiumPage from "@/pages/PremiumPage";

// HomeRedirect component to handle redirecting authenticated users
function HomeRedirect() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const checkUserType = async () => {
      if (!user || (location.pathname !== '/' && location.pathname !== '/dashboard')) return;
      
      try {
        console.log("Checking user type for user:", user.id);
        console.log("Current pathname:", location.pathname);
        
        // Check if this is from a claim flow first
        const registrationContext = sessionStorage.getItem('registrationContext');
        console.log("Registration context from sessionStorage:", registrationContext);
        
        const fromClaim = registrationContext === 'claim';
        console.log("Is from claim flow:", fromClaim);
        
        if (fromClaim) {
          console.log("User is from claim flow, redirecting to owner dashboard");
          // Clean up session storage
          sessionStorage.removeItem('registrationContext');
          sessionStorage.removeItem('userType');
          navigate('/owner/dashboard');
          return;
        }
        
        // Check if user is an admin first using the is_admin RPC function
        const { data: isAdmin, error: adminError } = await supabase.rpc(
          'is_admin', 
          { uid: user.id }
        );
          
        if (adminError) {
          console.error("Error checking admin status:", adminError);
          throw adminError;
        }
        
        // If admin, redirect to admin tools
        if (isAdmin) {
          console.log("User is an admin, redirecting to admin tools");
          navigate('/admin/tools');
          return;
        }
        
        // Respect explicit user type selection (from auth path) if present
        const selectedUserType = sessionStorage.getItem('userType');
        if (selectedUserType === 'family') {
          console.log("User selected family path, redirecting to family dashboard");
          navigate('/dashboard');
          return;
        }
        if (selectedUserType === 'owner' || selectedUserType === 'provider') {
          console.log("User selected provider path, redirecting to owner dashboard");
          navigate('/owner/dashboard');
          return;
        }
        
        // Check if user has any approved nursing home claims
        const { data: claims, error: claimsError } = await supabase
          .from('nursing_home_claims')
          .select('status')
          .eq('user_id', user.id)
          .eq('status', 'approved');
          
        if (claimsError) throw claimsError;
        
        console.log("User approved claims:", claims);
        
        if (claims && claims.length > 0) {
          console.log("User is a nursing home owner, redirecting to owner dashboard");
          // If the user has approved claims, they're a nursing home owner
          navigate('/owner/dashboard');
        } else {
          console.log("User is a regular user, redirecting to dashboard");
          // Otherwise, they're a regular user
          navigate('/dashboard');
        }
      } catch (error) {
        console.error("Error checking user type:", error);
        toast.error("Failed to determine user type", {
          description: "An error occurred while checking your account type. Redirecting to regular dashboard."
        });
        // Default to regular dashboard on error
        navigate('/dashboard');
      }
    };
    
    if (user) {
      checkUserType();
    }
  }, [user, navigate, location.pathname]);
  
  return null;
}

// Router content that includes hooks dependent on router context
function RouterContent() {
  // Initialize analytics (needs router context)
  useAnalytics();

  return (
    <>
      <ScrollToTop />
      <HomeRedirect />
    </>
  );
}

// Layout component that wraps all routes
function Layout({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider>
      <ComparisonProvider>
        <RedirectMiddleware>
          <RouterContent />
          {children}
        </RedirectMiddleware>
        <ComparisonBar />
        <ComparisonModal />
      </ComparisonProvider>
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout><Index /></Layout>,
  },
  {
    path: "/auth/login",
    element: <Auth />,
  },
  {
    path: "/auth/signup",
    element: <Auth />,
  },
  {
    path: "/auth/owner-register",
    element: <OwnerRegisterPage />,
  },
  {
    path: "/auth/verify-email",
    element: <EmailVerificationPage />,
  },
  {
    path: "/auth/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/auth/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/dashboard",
    element: <Layout><Dashboard /></Layout>,
  },
  {
    path: "/nursing-homes",
    element: <Layout><AllNursingHomes /></Layout>,
  },
  {
    path: "/favorites",
    element: <Layout><FavoritesPage /></Layout>,
  },
  {
    path: "/nursing-homes/map",
    element: <Layout><AllNursingHomesMapPage /></Layout>,
  },
  {
    path: "/nursing-homes/lookup/:nhSlug",
    element: <Layout><NursingHomeLookupPage /></Layout>,
  },
  {
    path: "/nursing-homes/:countySlug",
    element: <Layout><CountyPage /></Layout>,
  },
  {
    path: "/nursing-homes/:countySlug/:nursing_home_slug",
    element: <Layout><NursingHomePage /></Layout>,
  },
  {
    path: "/about",
    element: <Layout><AboutPage /></Layout>,
  },
  {
    path: "/press",
    element: <Layout><PressPage /></Layout>,
  },
  {
    path: "/careers",
    element: <Layout><CareersPage /></Layout>,
  },
  {
    path: "/legal/terms-of-use",
    element: <Layout><TermsPage /></Layout>,
  },
  {
    path: "/legal/privacy-policy",
    element: <Layout><PrivacyPolicy /></Layout>,
  },
  {
    path: "/legal/cookie-policy",
    element: <Layout><CookiesPage /></Layout>,
  },
  {
    path: "/fair-deal",
    element: <Layout><FairDealHubPage /></Layout>,
  },
  {
    path: "/fair-deal-calculator",
    element: <Layout><CalculatorPage /></Layout>,
  },
  {
    path: "/waitlist",
    element: <Layout><WaitlistPage /></Layout>,
  },
  {
    path: "/thank-you",
    element: <Layout><ThankYouPage /></Layout>,
  },
  {
    path: "/guides",
    element: <Layout><GuidePage /></Layout>,
  },
  {
    path: "/guides/finding-nursing-home-ireland-2026",
    element: <Layout><DigitalGuidePage /></Layout>,
  },
  {
    path: "/consultations",
    element: <Layout><ConsultationsPage /></Layout>,
  },
  {
    path: "/consultations/expert/:id",
    element: <Layout><ExpertProfilePage /></Layout>,
  },
  {
    path: "/blog",
    element: <Layout><BlogPage /></Layout>,
  },
  {
    path: "/apply-expert",
    element: <Layout><ExpertApplicationPage /></Layout>,
  },
  {
    path: "/expert-marketplace-terms",
    element: <Layout><ExpertMarketplaceTermsPage /></Layout>,
  },
  {
    path: "/apply-expert/thank-you",
    element: <Layout><ExpertApplicationThankYouPage /></Layout>,
  },
  {
    path: "/admin/tools",
    element: <Layout><AdminToolsPage /></Layout>,
  },
  {
    path: "/admin/expert-applications",
    element: <Layout><ExpertApplicationsPage /></Layout>,
  },
  {
    path: "/admin/claims",
    element: <Layout><ClaimsManagementPage /></Layout>,
  },
  {
    path: "/admin/guides",
    element: <Layout><AdminGuidePage /></Layout>,
  },
  // Owner routes
  {
    path: "/owner/dashboard",
    element: <Layout><OwnerDashboardPage /></Layout>,
  },
  {
    path: "/owner/nursing-homes/:nhSlug",
    element: <Layout><OwnerNursingHomePage /></Layout>,
    children: [
      { path: "", element: <OverviewPage /> },
      { path: "profile", element: <ProfilePage /> },
      { path: "media", element: <MediaPage /> },
     { path: "leads", element: <LeadsPage /> },
      { path: "settings", element: <OwnerSettingsPage /> },
    ]
  },
  {
    path: "/owner/subscription",
    element: <Layout><SubscriptionPage /></Layout>,
  },
  {
    path: "/premium",
    element: <Layout><PremiumPage /></Layout>,
  },
  {
    path: "/settings",
    element: <Layout><SettingsPage /></Layout>,
  },
  {
    path: "/email-test",
    element: <Layout><EmailTestPage /></Layout>,
  },
  {
    path: "/claim",
    element: <GeneralClaimPage />,
  },
  {
    path: "/claim/:nursingHomeSlug",
    element: <ClaimNursingHomePage />,
  },
  {
    path: "/co/claim",
    element: <ClaimNursingHomePage />,
  },
  {
    path: "/not-found",
    element: <Layout><NotFound /></Layout>,
  },
  {
    path: "*",
    element: <Layout><NotFound /></Layout>,
  },
]);

export default router;
